import React, { useEffect, useRef } from 'react';

const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_TEST_SITE_KEY = '1x00000000000000000000AA';

let scriptPromise;

const loadTurnstile = () => {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.turnstile) {
          resolve(window.turnstile);
        } else {
          reject(new Error('Turnstile did not initialize.'));
        }
      };
      script.onerror = () => reject(new Error('Turnstile could not be loaded.'));
      document.head.appendChild(script);
    }).catch((error) => {
      scriptPromise = undefined;
      throw error;
    });
  }

  return scriptPromise;
};

const Turnstile = ({ onVerify, onUnavailable }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onUnavailableRef = useRef(onUnavailable);
  const configuredSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const siteKey =
    configuredSiteKey ||
    (process.env.NODE_ENV === 'development' ? TURNSTILE_TEST_SITE_KEY : '');

  onVerifyRef.current = onVerify;
  onUnavailableRef.current = onUnavailable;

  useEffect(() => {
    let cancelled = false;

    if (!siteKey) {
      onUnavailableRef.current(
        'Verification is temporarily unavailable. Please contact us by email.'
      );
      return undefined;
    }

    loadTurnstile()
      .then((turnstile) => {
        turnstile.ready(() => {
          if (cancelled || !containerRef.current) {
            return;
          }

          widgetIdRef.current = turnstile.render(containerRef.current, {
            sitekey: siteKey,
            action: 'contact',
            theme: 'dark',
            callback: (token) => onVerifyRef.current(token),
            'expired-callback': () => onVerifyRef.current(''),
            'timeout-callback': () => onVerifyRef.current(''),
            'error-callback': () => {
              onVerifyRef.current('');
              onUnavailableRef.current(
                'Verification failed to load. Please refresh and try again.'
              );
            },
          });
        });
      })
      .catch(() => {
        if (!cancelled) {
          onUnavailableRef.current(
            'Verification failed to load. Please refresh and try again.'
          );
        }
      });

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [siteKey]);

  return <div ref={containerRef} className="turnstile-widget" />;
};

export default Turnstile;
